import React, { useState } from "react";
import { ActionIcon, Button, Popover, Stack, Box, Text, Group } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { MdDateRange } from "react-icons/md";
import { endOfDay, ensureChronological, startOfDay } from "./chartUtils";
import { usePopoverManager } from "./PopoverManager";

interface DateRangeControlProps {
    value: [Date, Date];
    onChange: (next: [Date, Date]) => void;
    disabled?: boolean;
    maxDate?: Date;
}

export const DateRangeControl: React.FC<DateRangeControlProps> = ({ value, onChange, disabled, maxDate = new Date() }) => {
    const { isPopoverOpen, togglePopover, setOpenPopover } = usePopoverManager();
    const [tempStart, setTempStart] = useState<Date | null>(value[0]);
    const [tempEnd, setTempEnd] = useState<Date | null>(value[1]);

    const popoverId = "date-range-filter";
    const opened = isPopoverOpen(popoverId);

    const handleToggle = () => {
        const wasOpened = opened;
        togglePopover(popoverId);

        if (!wasOpened) {
            setTempStart(value[0]);
            setTempEnd(value[1]);
        }
    };

    const handleApply = () => {
        if (tempStart && tempEnd) {
            const [start, end] = ensureChronological(tempStart, tempEnd);
            onChange([start, end]);
            setOpenPopover(null);
        }
    };

    const handleCancel = () => {
        setTempStart(value[0]);
        setTempEnd(value[1]);
        setOpenPopover(null);
    };

    const handleClose = () => {
        // Only close if this popover is actually the one that's open
        if (opened) {
            handleCancel();
        }
    };

    return (
        <Popover
            opened={opened}
            withArrow={false}
            closeOnClickOutside={false}
            closeOnEscape={true}
            trapFocus={false}
            withinPortal={true}
            position="bottom-end"
            onClose={handleClose}
            styles={{
                dropdown: {
                    padding: "12px",
                    minWidth: "320px",
                    maxWidth: "400px",
                    border: "2px solid #339af0",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(51, 154, 240, 0.15)",
                    backgroundColor: "#ffffff"
                }
            }}
        >
            <Popover.Target>
                <ActionIcon variant="light" size="sm" onClick={handleToggle} disabled={disabled}>
                    <MdDateRange size={16} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
                <div onMouseDown={event => event.stopPropagation()} onMouseUp={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
                    <Box w={280}>
                        <Text size="sm" fw={500} mb="md">
                            Select Date Range
                        </Text>
                        <Stack gap="md">
                            <DatePickerInput
                                label="From Date"
                                size="sm"
                                value={tempStart}
                                maxDate={tempEnd ?? maxDate}
                                onChange={date => {
                                    if (date) {
                                        setTempStart(typeof date === "string" ? startOfDay(new Date(date)) : startOfDay(date));
                                    } else {
                                        setTempStart(null);
                                    }
                                }}
                                clearable={false}
                            />
                            <DatePickerInput
                                label="To Date"
                                size="sm"
                                value={tempEnd}
                                minDate={tempStart ?? undefined}
                                maxDate={endOfDay(maxDate)}
                                onChange={date => {
                                    if (date) {
                                        setTempEnd(typeof date === "string" ? endOfDay(new Date(date)) : endOfDay(date));
                                    } else {
                                        setTempEnd(null);
                                    }
                                }}
                                clearable={false}
                            />
                        </Stack>
                        <Stack gap="xs" mt="md">
                            <Group gap="xs" grow>
                                <Button size="sm" onClick={handleApply} variant="filled" color="blue">
                                    Apply
                                </Button>
                                <Button size="sm" onClick={handleCancel} variant="subtle">
                                    Cancel
                                </Button>
                            </Group>
                        </Stack>
                    </Box>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
};
